import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GridColumn } from "semantic-ui-react";

import { AttachmentsPreview, DATE_FORMATS_FNS } from "components";
import { useSnoozeDepartmentOptions, useSnoozeTypeOptions } from "components/SnoozeModal/hooks";
import { getSnoozeStatusOptions } from "components/SnoozeModal/util";
import { SNOOZE_STATUSES } from "models";
import { SnoozeActions } from "modules/AppointmentDetails/components/SnoozedItems/components";
import { useAppointmentDealer } from "modules/AppointmentDetails/hooks";
import { getAllSnoozeAttachments } from "modules/AppointmentDetails/utils";
import { formatDate } from "utils/dateHelperFunctions";

import type { Appointment, Intervention, QuestionResult, SNOOZE_DEPARTMENTS, SNOOZE_TYPES, Snooze } from "models";

type SnoozeItemProps = {
  questionResult?: QuestionResult;
  intervention?: Intervention;
  noBorder?: boolean;
  appointment: Appointment;
  snoozedItems: (QuestionResult | Intervention)[];
};

export const SnoozeItem = ({ intervention, questionResult, appointment, noBorder, snoozedItems }: SnoozeItemProps) => {
  const { t } = useTranslation();
  const [showMedia, setShowMedia] = useState(false);

  const appointmentDealer = useAppointmentDealer(appointment);
  const { snoozeTypeOptions } = useSnoozeTypeOptions(appointmentDealer);
  const { snoozeStatusOptions } = getSnoozeStatusOptions(t);
  const { snoozeDepartmentOptions } = useSnoozeDepartmentOptions();

  const toggleShowMedia = () => setShowMedia(prev => !prev);

  const getSnoozeType = (snoozeType?: SNOOZE_TYPES) => (snoozeType ? snoozeTypeOptions.find(type => type.value === snoozeType)?.text || "" : "");
  const getSnoozeStatus = (snoozeStatus?: SNOOZE_STATUSES | null) => (snoozeStatus ? snoozeStatusOptions.find(status => status.value === snoozeStatus)?.text || "" : "");
  const getSnoozeDepartment = (snoozeDepartment?: SNOOZE_DEPARTMENTS | null) =>
    snoozeDepartment ? snoozeDepartmentOptions.find(status => status.value === snoozeDepartment)?.text || "" : "";

  const getSortedSnoozeHistory = (snoozeHistory?: Snooze[]) => snoozeHistory?.sort((history1, history2) => history2.created_on.localeCompare(history1.created_on));
  const sortedSnoozeHistory = getSortedSnoozeHistory(intervention?.snooze_history || questionResult?.snooze_history);

  const snoozeItem = !!sortedSnoozeHistory?.length && sortedSnoozeHistory[0].snooze_status_id !== SNOOZE_STATUSES.Deleted ? sortedSnoozeHistory[0] : null;
  const attachments = getAllSnoozeAttachments(intervention?.snooze_history ?? questionResult?.snooze_history ?? []);

  return (
    <>
      <GridColumn className="content">
        <p>{intervention?.title || questionResult?.title || ""}</p>
      </GridColumn>
      <GridColumn className="content">
        <p>{getSnoozeType(snoozeItem?.snooze_type_id)}</p>
      </GridColumn>
      <GridColumn className="content">
        <p>{snoozeItem?.snooze_date ? formatDate(snoozeItem.snooze_date, DATE_FORMATS_FNS.dateMonthYear) : ""}</p>
      </GridColumn>
      <GridColumn className="content">
        <p>{getSnoozeStatus(snoozeItem?.snooze_status_id)}</p>
      </GridColumn>
      <GridColumn className="content">
        <p>{getSnoozeDepartment(snoozeItem?.snooze_department_id)}</p>
      </GridColumn>
      <GridColumn className="content">
        <p>{snoozeItem?.note || ""}</p>
      </GridColumn>
      <GridColumn className="content last">
        {snoozeItem && (
          <SnoozeActions
            snoozeItem={snoozeItem}
            appointment={appointment}
            attachments={attachments}
            snoozedItems={snoozedItems}
            intervention={intervention}
            questionResult={questionResult}
            toggleShowMedia={toggleShowMedia}
          />
        )}
      </GridColumn>
      {showMedia && (
        <div className="snooze-images">
          <AttachmentsPreview attachments={attachments} />
        </div>
      )}
      <div className={`divider ${noBorder ? "no-border" : ""}`} />
    </>
  );
};
